(() => {
  const PREVIEW_CLASS = 'dc-cart-upload-preview';
  const LINK_CLASS = 'dc-cart-upload-preview-link';
  const ORDER_ATTRIBUTE_PREFIX = '_DC P';
  const ADMIN_ATTRIBUTE_PREFIX = '__PERSONALISATIE';
  let syncTimer = 0;
  let syncing = false;
  let lastSuccessfulSignature = '';

  function cleanText(value) {
    return String(value || '').replace(/\s+/g, ' ').trim();
  }

  function localeText(nl, en, de) {
    const lang = String(document.documentElement.lang || 'nl').toLowerCase().slice(0, 2);
    if (lang === 'en') return en;
    if (lang === 'de') return de;
    return nl;
  }

  function normalizedLabel(property) {
    return cleanText(property.querySelector('dt')?.textContent).toLowerCase();
  }

  function altTextForRow(row) {
    const href = row.querySelector('.cart-items__title')?.getAttribute('href') || '';
    if (href.includes('/products/gepersonaliseerde-foto-sleutelhanger')) {
      return localeText(
        'Geüploade foto voor je gepersonaliseerde sleutelhanger',
        'Uploaded photo for your personalised keyring',
        'Hochgeladenes Foto für deinen personalisierten Schlüsselanhänger'
      );
    }
    if (href.includes('/products/utrecht-domtoren-portemonneekaart')) {
      return localeText(
        'Geüploade foto voor de achterkant van je portemonneekaart',
        'Uploaded photo for the back of your wallet card',
        'Hochgeladenes Foto für die Rückseite deiner Brieftaschenkarte'
      );
    }
    if (href.includes('/products/utrecht-domtoren-dogtag-sleutelhanger')) {
      return localeText(
        'Geüploade foto voor de achterkant van je dogtag',
        'Uploaded photo for the back of your dog tag',
        'Hochgeladenes Foto für die Rückseite deines Dogtags'
      );
    }
    return localeText(
      'Geüploade foto voor je gepersonaliseerde product',
      'Uploaded photo for your personalised product',
      'Hochgeladenes Foto für dein personalisiertes Produkt'
    );
  }

  function enhanceProperty(property) {
    if (!(property instanceof HTMLElement)) return;
    const label = normalizedLabel(property);
    if (!label.includes('foto') && !label.includes('photo')) return;

    const link = property.querySelector('dd a[href*="/uploads/"]');
    if (!(link instanceof HTMLAnchorElement)) return;

    const row = property.closest('.cart-items__table-row, .cart-items__item, .cart-item, [data-cart-item]') || property.parentElement;
    const ariaLabel = localeText('Open de geüploade foto', 'Open the uploaded photo', 'Hochgeladenes Foto öffnen');
    const existingImage = link.querySelector(`.${PREVIEW_CLASS}`);
    if (existingImage instanceof HTMLImageElement) {
      existingImage.alt = altTextForRow(row || property);
      link.setAttribute('aria-label', ariaLabel);
      return;
    }

    const filename = link.textContent?.trim() || localeText('Geüploade foto', 'Uploaded photo', 'Hochgeladenes Foto');
    const image = document.createElement('img');
    image.className = PREVIEW_CLASS;
    image.src = link.href;
    image.alt = altTextForRow(row || property);
    image.loading = 'lazy';
    image.decoding = 'async';

    image.addEventListener('error', () => {
      link.classList.remove(LINK_CLASS);
      link.removeAttribute('aria-label');
      link.textContent = filename;
    }, { once: true });

    link.textContent = '';
    link.classList.add(LINK_CLASS);
    link.setAttribute('aria-label', ariaLabel);
    link.appendChild(image);
  }

  function enhance(root = document) {
    if (!(root instanceof Document || root instanceof DocumentFragment || root instanceof Element)) return;
    root.querySelectorAll('.cart-items__property, .product-details__item').forEach(enhanceProperty);
  }

  function propertyValue(property) {
    const dd = property.querySelector('dd');
    if (!(dd instanceof HTMLElement)) return '';
    const link = dd.querySelector('a[href]');
    if (link instanceof HTMLAnchorElement && (link.href.includes('/uploads/') || link.href.includes('/files/'))) {
      return link.href;
    }
    return cleanText(dd.textContent);
  }

  function collectOrderAttributes() {
    const adminAttributes = {};
    for (let i = 1; i <= 20; i += 1) {
      const code = `${ADMIN_ATTRIBUTE_PREFIX} ${String(i).padStart(2, '0')}`;
      // New compact production block.
      adminAttributes[code] = '';
      // Clear legacy split fields from carts that already contain them.
      adminAttributes[`${code} - PRODUCT`] = '';
      adminAttributes[`${code} - GEGEVENS`] = '';
      adminAttributes[`${code} - FOTO`] = '';
    }

    const rows = [];
    const seenRows = new Set();

    document.querySelectorAll('.cart-items__table-row').forEach((row) => {
      if (!(row instanceof HTMLElement) || row.classList.contains('cart-items__nested-line')) return;
      const rowKey = row.dataset.key || row.id || String(rows.length);
      if (seenRows.has(rowKey)) return;
      seenRows.add(rowKey);
      rows.push(row);
    });

    let sequence = 0;
    for (const row of rows) {
      const properties = [...row.querySelectorAll('.cart-items__property')].filter((property) => {
        const label = cleanText(property.querySelector('dt')?.textContent).replace(/:$/, '');
        return label && propertyValue(property);
      });

      sequence += 1;
      if (sequence > 20) break;

      const adminCode = `${ADMIN_ATTRIBUTE_PREFIX} ${String(sequence).padStart(2, '0')}`;
      const title = cleanText(row.querySelector('.cart-items__title')?.textContent) || 'Product';
      const variant = cleanText(row.dataset.dcVariant);
      const production = [`Product: ${title}`];

      let optionCount = 0;
      try {
        const options = JSON.parse(row.dataset.dcOptions || '[]');
        if (Array.isArray(options)) {
          for (const option of options) {
            const name = cleanText(option?.name);
            const value = cleanText(option?.value);
            if (!name || !value || value.toLowerCase() === 'default title') continue;
            production.push(`${name}: ${value}`);
            optionCount += 1;
          }
        }
      } catch {}

      // Fallback for themes/products where named option data isn't available.
      if (optionCount === 0 && variant && variant.toLowerCase() !== 'default title') {
        production.push(`Variant: ${variant}`);
      }

      const quantityInput = row.querySelector('input[type="number"]');
      const quantity = quantityInput instanceof HTMLInputElement ? cleanText(quantityInput.value) : '';
      if (quantity && quantity !== '1') production.push(`Aantal: ${quantity}`);

      for (const property of properties) {
        const label = cleanText(property.querySelector('dt')?.textContent).replace(/:$/, '');
        const value = propertyValue(property);
        if (!label || !value) continue;

        const normalized = label.toLowerCase();
        const isUploadedPhoto =
          (normalized === 'foto' || normalized === 'photo' || normalized.includes('foto achterkant') || normalized.includes('photo back')) &&
          /^https?:\/\//i.test(value);

        if (isUploadedPhoto) {
          production.push(`Foto: ${value}`);
        } else {
          production.push(`${label}: ${value}`);
        }
      }

      adminAttributes[adminCode] = production.join(' | ').slice(0, 1800);
    }

    return { adminAttributes };
  }
  async function syncOrderAttributes() {
    if (syncing) return;
    const collected = collectOrderAttributes();
    const adminDesired = collected.adminAttributes;
    const signature = JSON.stringify({ adminDesired });
    if (signature === lastSuccessfulSignature) return;

    syncing = true;
    try {
      const root = window.Shopify?.routes?.root || '/';
      const cartResponse = await fetch(`${root}cart.js`, {
        headers: { Accept: 'application/json' },
        credentials: 'same-origin'
      });
      if (!cartResponse.ok) return;
      const cart = await cartResponse.json();
      const current = cart?.attributes || {};
      const updates = {};

      // Remove the old duplicated customer-visible _DC Pxx copy.
      for (const [key, value] of Object.entries(current)) {
        if (key.startsWith(ORDER_ATTRIBUTE_PREFIX) && value !== '') updates[key] = '';
      }

      // Keep only the compact private admin payload used by the order workflow.
      for (const [key, value] of Object.entries(adminDesired)) {
        updates[key] = value;
      }

      if (Object.keys(updates).length) {
        const updateResponse = await fetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json'
          },
          credentials: 'same-origin',
          body: JSON.stringify({ attributes: updates })
        });
        if (!updateResponse.ok) return;
      }

      lastSuccessfulSignature = signature;
    } catch (error) {
      console.warn('[Domstad] Personalisatiegegevens konden niet naar de winkelwagen worden gesynchroniseerd.', error);
    } finally {
      syncing = false;
    }
  }
  function scheduleOrderSync(delay = 300) {
    window.clearTimeout(syncTimer);
    syncTimer = window.setTimeout(syncOrderAttributes, delay);
  }

  function start() {
    enhance(document);
    scheduleOrderSync(500);

    document.addEventListener('theme-drawer:open', () => scheduleOrderSync(350));
    document.addEventListener('input', (event) => {
      if (event.target instanceof Element && event.target.closest('.cart-items__table-row')) scheduleOrderSync(500);
    });
    document.addEventListener('change', (event) => {
      if (event.target instanceof Element && event.target.closest('.cart-items__table-row')) scheduleOrderSync(350);
    });

    new MutationObserver((mutations) => {
      let cartChanged = false;
      for (const mutation of mutations) {
        for (const node of mutation.addedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches('.cart-items__property, .product-details__item')) enhanceProperty(node);
          enhance(node);
          if (node.matches('.cart-items__table-row, .cart-items__property') || node.querySelector('.cart-items__table-row, .cart-items__property')) {
            cartChanged = true;
          }
        }
        for (const node of mutation.removedNodes) {
          if (!(node instanceof Element)) continue;
          if (node.matches('.cart-items__table-row, .cart-items__property') || node.querySelector('.cart-items__table-row, .cart-items__property')) {
            cartChanged = true;
          }
        }
      }
      if (cartChanged) scheduleOrderSync(450);
    }).observe(document.documentElement, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
